import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type CreditPackage = {
  credits: number;
  price: number;
  label: string;
  promoDiscountPercentage?: number;
  promoDateEnd?: Date;
};

export const CREDITS_PACKAGES: CreditPackage[] = [
  {
    credits: 100,
    price: 10,
    label: "100",
    promoDiscountPercentage: 30,
    promoDateEnd: new Date("2025-10-31T23:59:59"),
  },
  {
    credits: 250,
    price: 22,
    label: "250",
    promoDiscountPercentage: 30,
    promoDateEnd: new Date("2025-10-31T23:59:59"),
  },
  {
    credits: 500,
    price: 40,
    label: "500",
    promoDiscountPercentage: 30,
    promoDateEnd: new Date("2025-10-31T23:59:59"),
  },
  {
    credits: 1000,
    price: 75,
    label: "1k",
    promoDiscountPercentage: 30,
    promoDateEnd: new Date("2025-10-31T23:59:59"),
  },
  {
    credits: 2500,
    price: 169,
    label: "2.5k",
    promoDiscountPercentage: 30,
    promoDateEnd: new Date("2025-10-31T23:59:59"),
  },
  {
    credits: 5000,
    price: 299,
    label: "5k",
    promoDiscountPercentage: 30,
    promoDateEnd: new Date("2025-10-31T23:59:59"),
  },
];

export const CREDITS_RETRIEVAL_COST = {
  PHONE: 10,
};

/**
 * Check if a promo code is currently valid
 */
export const isPromoValid = (pkg: CreditPackage): boolean => {
  if (!pkg.promoDiscountPercentage || !pkg.promoDateEnd) return false;
  return new Date() <= pkg.promoDateEnd;
};

/**
 * Calculate the discounted price for a package
 */
export const getDiscountedPrice = (pkg: CreditPackage): number => {
  if (!isPromoValid(pkg)) return pkg.price;
  return (
    Math.round(
      pkg.price * (1 - (pkg.promoDiscountPercentage || 0) / 100) * 100
    ) / 100
  );
};

/**
 * Get package with promo information
 */
export const getPackageWithPromo = (pkg: CreditPackage) => {
  const hasValidPromo = isPromoValid(pkg);
  const originalPrice = pkg.price;
  const finalPrice = hasValidPromo ? getDiscountedPrice(pkg) : originalPrice;
  const savings = hasValidPromo
    ? Math.round((originalPrice - finalPrice) * 100) / 100
    : 0;

  return {
    ...pkg,
    hasValidPromo,
    originalPrice,
    finalPrice,
    savings,
    discountPercentage: hasValidPromo ? pkg.promoDiscountPercentage || 0 : 0,
  };
};

type CreditsContextType = {
  creditsBalance: number;
  isLoading: boolean;
  refreshCredits: () => Promise<void>;
};

const CreditsContext = createContext<CreditsContextType>({
  creditsBalance: 0,
  isLoading: false,
  refreshCredits: async () => {},
});

export const useCredits = (): CreditsContextType => {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error("useCredits must be used within a CreditsProvider");
  }
  return context;
};

export const CreditsProvider = ({ children }: { children: ReactNode }) => {
  const api = useApi();
  const { teamSelected } = useTeams();
  const [creditsBalance, setCreditsBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCreditsBalance = useCallback(async () => {
    if (!teamSelected?.id) return;

    setIsLoading(true);
    try {
      const response = await api.get("/my/team/credits/balance");
      const balance = response.data as number;
      setCreditsBalance(balance);
    } catch (error) {
      // Fallback to team data if API fails
      const fallbackBalance = teamSelected.creditsBalance || 0;
      setCreditsBalance(fallbackBalance);
    } finally {
      setIsLoading(false);
    }
  }, [api, teamSelected?.id, teamSelected?.creditsBalance]);

  const refreshCredits = useCallback(async () => {
    await fetchCreditsBalance();
  }, [fetchCreditsBalance]);

  // Auto fetch credits every minute
  useEffect(() => {
    if (!teamSelected?.id) return;

    // Initial balance
    setCreditsBalance(teamSelected.creditsBalance || 0);

    // Set up interval for auto refresh every minute
    const intervalId = setInterval(() => {
      // Only fetch if tab is visible to avoid unnecessary API calls
      if (!document.hidden) {
        fetchCreditsBalance();
      }
    }, 60000); // 60 seconds = 1 minute

    // Also fetch when tab becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCreditsBalance();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup interval and event listener on unmount or team change
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [teamSelected?.id, fetchCreditsBalance, teamSelected?.creditsBalance]);

  const value = useMemo(
    () => ({
      creditsBalance,
      isLoading,
      refreshCredits,
    }),
    [creditsBalance, isLoading, refreshCredits],
  );

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
};
